import { useAuth } from "@workos-inc/authkit-nextjs/components";
import { i18nConfig } from "i18nConfig";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useCompany } from "~/components/providers/CompanyProvider";
import { useFeatureFlags } from "~/hooks/useFeatureFlags";
import { offstreetClient } from "~/services/offstreetApi";

export const useAreParkerAccountsRolledOut = () => {
	const { globalParkerFlag } = useFeatureFlags();
	const { company, isFromSubdomain } = useCompany();
	const isCompanyRolledOut = !!globalParkerFlag || (isFromSubdomain && !!company?.isOptionalParkerAccountsEnabled);
	const utils = offstreetClient.useUtils();
	const auth = useAuth({ ensureSignedIn: false });
	const userId = auth.user?.id;
	const isSharedPortal = !isFromSubdomain;
	const isSharedPortalCompanyPage = isSharedPortal && !!company;

	const isRemembered = useSyncExternalStore(
		() => () => {},
		() => document.cookie.split("; ").includes("offstreet-portal-accounts=1"),
		() => undefined,
	);

	const flagsUserId = useRef(userId);
	useEffect(() => {
		if (flagsUserId.current === userId) {
			return;
		}
		flagsUserId.current = userId;
		void utils.featureFlags.getFeatureFlags.invalidate();
	}, [userId, utils]);

	useEffect(() => {
		if (!isSharedPortalCompanyPage || !isCompanyRolledOut || isRemembered !== false) {
			return;
		}
		const secure = window.location.protocol === "https:" ? "; Secure" : "";
		document.cookie = `offstreet-portal-accounts=1; Path=/; Max-Age=3600; SameSite=Lax${secure}`;
	}, [isSharedPortalCompanyPage, isCompanyRolledOut, isRemembered]);

	const sharedPortalPage = getSharedPortalPage(usePathname());
	return (
		isCompanyRolledOut ||
		(isSharedPortal && sharedPortalPage === "account") ||
		(isSharedPortal && sharedPortalPage === "general" && !!isRemembered && !auth.user)
	);
};

const getSharedPortalPage = (pathname: string) => {
	const segments = pathname.split("/").filter(Boolean);
	const [section] = i18nConfig.locales.includes(segments[0] ?? "") ? segments.slice(1) : segments;
	if (section === undefined || section === "support") {
		return "general";
	}
	return [
		"login",
		"signup",
		"forgot-password",
		"reset-password",
		"mfa",
		"mfa-enroll",
		"verify-email",
		"account",
		"permits",
		"transactions",
	].includes(section)
		? "account"
		: undefined;
};
